import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';

@Component({
  selector: 'app-pagination',
  standalone: true,
  imports: [CommonModule, TranslocoDirective, RouterModule],
  templateUrl: './pagination.component.html',
  styleUrl: './pagination.component.css',
})
export class PaginationComponent implements OnInit, OnChanges {
  @Input() totalData: number | undefined = 0;
  @Input() pageSize: number = 10;
  @Input() currentPage: number = 1;

  @Output() public pageSelect: EventEmitter<number> = new EventEmitter();

  public totalPages: number = 0;

  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.updatePagination();
  }

  ngOnInit() {
    // if (this.totalData === undefined) {
    //   this.totalPages = 1;
    //   return;
    // }
    // this.totalPages = Math.ceil(this.totalData / this.pageSize);

    this._activatedRoute.queryParams.subscribe((params) => {
      const page = parseInt(params['page'], 10);
      this.currentPage = !isNaN(page) && page >= 1 ? page : 1;
      this.updatePagination();
    });
  }

  updatePagination(): void {
    this.totalPages = this.totalData
      ? Math.ceil(this.totalData / this.pageSize)
      : 1;
  }

  /**
   * Returns an array representing the pagination range.
   * The array contains numbers for page buttons and strings ('...') for ellipsis.
   */
  getPaginationRange(): (number | string)[] {
    const range: (number | string)[] = [];

    // If total pages is small, simply return all pages.
    if (this.totalPages <= 7) {
      for (let i = 1; i <= this.totalPages; i++) {
        range.push(i);
      }
      return range;
    }

    // If current page is near the beginning: show first 5 pages, ellipsis, last page.
    if (this.currentPage <= 4) {
      for (let i = 1; i <= 5; i++) {
        range.push(i);
      }
      range.push('...');
      range.push(this.totalPages);
    }
    // If current page is near the end: show first page, ellipsis, last 5 pages.
    else if (this.currentPage > this.totalPages - 4) {
      range.push(1);
      range.push('...');
      for (let i = this.totalPages - 4; i <= this.totalPages; i++) {
        range.push(i);
      }
    }
    // Otherwise, we're somewhere in the middle: show first page, ellipsis,
    // one page before, current, one page after, ellipsis, last page.
    else {
      range.push(1);
      range.push('...');
      range.push(this.currentPage - 1);
      range.push(this.currentPage);
      range.push(this.currentPage + 1);
      range.push('...');
      range.push(this.totalPages);
    }

    return range;
  }

  selectPage(page: number | string) {
    if (
      typeof page === 'string' ||
      page < 1 ||
      page > this.totalPages ||
      page === this.currentPage
    )
      return;
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: { page },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.pageSelect.emit(page);
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.selectPage(this.currentPage - 1);
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.selectPage(this.currentPage + 1);
    }
  }

  protected onNavigateToPage(page: number): void {
    this.updatePageParam({
      page: page,
    });
  }

  private updatePageParam(page: Partial<IPageNumber>): void {
    const currentParams = this._activatedRoute.snapshot.queryParams;
    const updatedParams = { ...currentParams, ...page };

    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: updatedParams,
      queryParamsHandling: 'merge',
      replaceUrl: true, // replaces history state instead of pushing a new one
    });
  }

  get pageRange(): { start: number; end: number } {
    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.totalData ?? 0);
    return { start, end };
  }
}

export interface IPageNumber {
  page: number;
}
